import { useEffect, useMemo, useRef, useState } from "react"

import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js"
import { Bar } from "react-chartjs-2"
import { useSelector } from "react-redux"

import { countryChartOptions } from "../../../DashboardPage/components/chartsOptions"
import { countryFlagAxisPlugin } from "../../../DashboardPage/components/chartsPlagins"
import { countryName } from "../../utils/records"

ChartJS.register(LinearScale, CategoryScale, BarElement, Tooltip, Legend)

// Created on <body> by countryChartOptions' external tooltip (shared with Prevent).
const TOOLTIP_ID = "chartjs-tooltip_country"

// Bar colours copied verbatim from Prevent's Alerts by Country (AlertsCountryChart):
// brand blue in dark, the light blue #93C1FD in light; non-hovered bars dim to 0.5.
const COLORS = {
  dark: { color: "rgba(34, 90, 234, 1)", hover: "rgba(34, 90, 234, 0.5)" },
  light: { color: "rgba(147, 193, 253, 1)", hover: "rgba(147, 193, 253, 0.5)" }
}

// The Alerts by Country chart, ported for lookups: a horizontal bar per country (flag + name on
// the y-axis), the same options/tooltip/hover-dim behaviour. `rows` are { key, count, value },
// one per by-country/ row.
const CountryChart = ({ rows = [], range }) => {
  const theme = useSelector((s) => s.nav.theme)
  const chartRef = useRef(null)
  const [hoverIndex, setHoverIndex] = useState(null)

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && chartRef.current) chartRef.current.update()
  }
  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  useEffect(() => () => document.getElementById(TOOLTIP_ID)?.remove(), [])

  const options = useMemo(
    () => countryChartOptions(range || [null, null], setHoverIndex, theme),
    [range, theme]
  )

  const c = COLORS[theme] || COLORS.light
  const data = {
    labels: rows.map((r) => countryName(r.key)),
    datasets: [
      {
        data: rows.map((r) => r.count),
        count: rows.map((r) => r.count),
        volume: rows.map((r) => r.value),
        codes: rows.map((r) => r.key),
        backgroundColor: rows.map((_, i) =>
          hoverIndex !== null && hoverIndex !== i ? c.hover : c.color
        ),
        borderRadius: [{ topLeft: 0, topRight: 2, bottomLeft: 0, bottomRight: 2 }],
        borderSkipped: false,
        pointHoverRadius: 2,
        barThickness: 22,
        yAxisID: "y"
      }
    ]
  }

  const height = 360

  return (
    <Bar
      key={`${theme}-${height}`}
      ref={chartRef}
      options={options}
      data={data}
      height={height}
      plugins={[countryFlagAxisPlugin]}
    />
  )
}

export default CountryChart
